import React from 'react';
import { ArrowDownNarrowWide } from 'lucide-react';
import { RobbieMark, TruncatedText } from '../../ui';
import { OutreachAction, SupplierThread } from './types';
import { timeAgo } from '../../../hooks/outreachMappers';
import { ActionIndex, actionsForThread } from '../../../hooks/outreachActionModel';
import ThreadStatusPill from './ThreadStatusPill';
import ContactEmailBadge from '../../suppliers/ContactEmailBadge';

/**
 * Outreach thread list, sorted by what the buyer can do rather than by recency
 * (the parent hook does the ordering, and holds it still while the buyer works
 * — rows update in place, they never move under the cursor).
 *
 * A row with an open action carries Robbie's read of it — the action's title
 * in violet, behind the mascot mark, with a count badge. A row with nothing
 * open falls back to the plain message snippet in body grey: no mark, no
 * badge, no invented suggestion.
 */
interface ThreadListProps {
  threads: SupplierThread[];
  activeThreadId?: string;
  actionIndex: ActionIndex;
  onSelectThread: (id: string) => void;
}

const ThreadRow: React.FC<{
  thread: SupplierThread;
  active: boolean;
  actions: OutreachAction[];
  onSelect: () => void;
}> = ({ thread, active, actions, onSelect }) => {
  // An un-started row's action is an opening ask nobody has answered yet, not
  // a read of a conversation — it previews plainly, without mark or count.
  const notSent = thread.status === 'not_sent';
  const read = notSent ? undefined : actions[0];
  const fallback = (notSent && actions[0]?.title) || thread.summary;
  // The pill already says "Not sent"; the meta line does not say it twice.
  const activity = timeAgo(thread.lastActivityIso);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`block w-full border-b border-line-divider px-[12px] py-s4 text-left ${
        active ? 'border-l-[2.5px] border-l-action bg-action-bg' : 'hover:bg-surface-hover'
      }`}
    >
      <div className="flex items-center gap-s2">
        <TruncatedText
          focusable={false}
          className={`min-w-0 flex-1 text-[12.5px] text-ink ${active ? 'font-bold' : 'font-semibold'}`}
          text={thread.supplierName}
        />
        <ContactEmailBadge
          variant="chip"
          hasContactEmail={thread.hasContactEmail}
          findingContact={thread.findingContact}
          noEmailTitle="No contact email — add one to enable outreach"
        />
        {actions.length > 0 && !notSent && (
          <span className="shrink-0 rounded-round border border-robbie-border bg-robbie-bg px-s2 py-[1px] font-mono text-[10px] font-bold text-robbie-ink">
            {actions.length}
          </span>
        )}
        <ThreadStatusPill status={thread.status} />
      </div>

      {read ? (
        <div className="mt-[4px] flex items-start gap-[5px]">
          <RobbieMark size="xxs" className="mt-[2px]" aria-label="Robbie's read of this thread" />
          <TruncatedText
            focusable={false}
            lines={2}
            className="min-w-0 flex-1 text-meta font-semibold leading-[1.4] text-robbie-ink"
            text={read.title}
          />
        </div>
      ) : (
        <TruncatedText
          as="div"
          focusable={false}
          className="mt-[4px] text-meta leading-[1.4] text-ink-secondary"
          text={fallback}
        />
      )}

      <div className="mt-[4px] text-[10.5px] text-ink-muted">
        {[thread.componentName, thread.productCount ? `${thread.productCount} products` : null, activity].filter(Boolean).join(' · ')}
      </div>
    </button>
  );
};

const ThreadList: React.FC<ThreadListProps> = ({ threads, activeThreadId, actionIndex, onSelectThread }) => (
  <div className="flex flex-col overflow-hidden border-r border-line max-md:border-r-0">
    <div className="flex shrink-0 items-center justify-between gap-s2 border-b border-line-divider-soft px-[12px] py-[9px]">
      <span className="text-body-sm font-bold text-ink">Threads · {threads.length}</span>
      <span
        className="inline-flex shrink-0 items-center gap-s1 text-[10.5px] font-medium text-ink-muted"
        title="I sorted these by what needs you when you opened the step, and I hold that order while you work — rows update where they are instead of jumping. I re-sort next time you come back."
      >
        <ArrowDownNarrowWide className="h-[11px] w-[11px]" />
        Sorted by what needs you
      </span>
    </div>
    <div className="flex-1 overflow-y-auto">
      {threads.map((thread) => (
        <ThreadRow
          key={thread.id}
          thread={thread}
          active={thread.id === activeThreadId}
          actions={actionsForThread(actionIndex, thread)}
          onSelect={() => onSelectThread(thread.id)}
        />
      ))}
    </div>
  </div>
);

export default ThreadList;
